// The little clock over a turn timer.
//
// Each time a Start of Turn or End of Turn Timer counts down, a small clock face pops up just above it and
// its hand sweeps once round, then it fades. It always faces the camera. How long, how big and what color
// are in src/config.ts under turn.tickClock.
import * as THREE from 'three';
import { CONFIG } from '../../config.ts';

let shared: { face: THREE.Texture; hand: THREE.Texture } | null = null;
function textures() {
  if (shared) return shared;
  const size = 128;
  const mid = size / 2;
  const canvas = () => {
    const c = document.createElement('canvas');
    c.width = c.height = size;
    return [c, c.getContext('2d')!] as const;
  };
  // the face: a dark dial with a bright rim and twelve marks (drawn white, tinted by the material)
  const [fc, f] = canvas();
  f.beginPath();
  f.arc(mid, mid, 54, 0, Math.PI * 2);
  f.fillStyle = 'rgba(30,36,48,0.82)';
  f.fill();
  f.lineWidth = 7;
  f.strokeStyle = '#fff';
  f.stroke();
  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * Math.PI * 2;
    const long = i % 3 === 0;
    f.beginPath();
    f.moveTo(mid + Math.sin(a) * (long ? 36 : 42), mid - Math.cos(a) * (long ? 36 : 42));
    f.lineTo(mid + Math.sin(a) * 47, mid - Math.cos(a) * 47);
    f.lineWidth = long ? 5 : 3;
    f.strokeStyle = long ? '#fff' : 'rgba(255,255,255,0.6)';
    f.stroke();
  }
  // the hand: from the middle straight up, with a hub. The sprite turns about its middle, so the hand turns with it.
  const [hc, h] = canvas();
  h.lineCap = 'round';
  h.beginPath();
  h.moveTo(mid, mid + 8);
  h.lineTo(mid, mid - 38);
  h.lineWidth = 8;
  h.strokeStyle = '#fff';
  h.stroke();
  h.beginPath();
  h.arc(mid, mid, 8, 0, Math.PI * 2);
  h.fillStyle = '#fff';
  h.fill();
  shared = { face: new THREE.CanvasTexture(fc), hand: new THREE.CanvasTexture(hc) };
  return shared;
}

interface Clock {
  group: THREE.Group;
  face: THREE.SpriteMaterial;
  hand: THREE.SpriteMaterial;
  born: number;
  y: number;
}

export class TimerClocks {
  private scene: THREE.Scene;
  private live: Clock[] = [];
  private now = 0;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  /** A timer at this spot on the table just counted down. */
  play(x: number, z: number) {
    const C = CONFIG.turn.tickClock;
    if (!C.enabled) return;
    const t = textures();
    const make = (map: THREE.Texture, color: THREE.ColorRepresentation) => new THREE.SpriteMaterial({ map, color, transparent: true, opacity: 0, depthTest: false, depthWrite: false });
    const face = make(t.face, C.color);
    const hand = make(t.hand, '#ffffff');
    const group = new THREE.Group();
    const a = new THREE.Sprite(face);
    const b = new THREE.Sprite(hand);
    a.renderOrder = 40;
    b.renderOrder = 41;
    group.add(a, b);
    group.position.set(x, C.height, z);
    group.scale.setScalar(0.01);
    this.scene.add(group);
    this.live.push({ group, face, hand, born: this.now, y: C.height });
  }

  /** `time` in seconds. */
  update(time: number) {
    this.now = time;
    const C = CONFIG.turn.tickClock;
    this.live = this.live.filter((c) => {
      const k = (time - c.born) / Math.max(0.2, C.seconds);
      if (k >= 1) {
        this.scene.remove(c.group);
        c.face.dispose();
        c.hand.dispose();
        return false;
      }
      const pop = Math.min(1, k / 0.14);
      const grow = 1 - (1 - pop) * (1 - pop); // quick at first, settling
      const fade = k > 0.78 ? 1 - (k - 0.78) / 0.22 : 1;
      // the hand: slow away, quick through the middle, slow home
      const s = Math.min(1, Math.max(0, (k - 0.1) / 0.7));
      const eased = s * s * (3 - 2 * s);
      c.hand.rotation = -eased * Math.PI * 2 * C.sweeps;
      c.face.opacity = 0.95 * fade * pop;
      c.hand.opacity = fade * pop;
      c.group.scale.setScalar(C.size * (0.6 + 0.4 * grow));
      c.group.position.y = c.y + k * 0.5;
      return true;
    });
  }

  dispose() {
    for (const c of this.live) {
      this.scene.remove(c.group);
      c.face.dispose();
      c.hand.dispose();
    }
    this.live = [];
  }
}
